import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../context/LanguageContext';
import { 
  ChevronLeft, Clock, Flame, Dumbbell, Share2, 
  ChefHat, Check, Droplet
} from 'lucide-react';
import ShareRecipeModal from '../components/ShareRecipeModal';

export default function RicettaDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const { t } = useTranslation();
  
  const [showShareModal, setShowShareModal] = useState(false);
  const [published, setPublished] = useState(false);
  
  // Get recipe data from navigation state
  const recipe = location.state?.recipe;
  const imageUrl = location.state?.imageUrl;

  // Pre-format the post body once so both share targets reuse the
  // same caption and the modal stays presentation-only.
  // Mirror the backend `/chef/publish-recipe` text format so a recipe
  // shared via the modal looks identical to one published directly
  // from ChefBot (ingredients + preparation steps included).
  const postText = useMemo(() => {
    if (!recipe) return '';
    let text = `🍳 **${recipe.nome}**\n\n`;
    const descrizione = (recipe.descrizione || '').trim();
    if (descrizione) text += `${descrizione}\n\n`;
    text += `⏱️ ${recipe.tempo_preparazione} | 🔥 ${recipe.calorie} kcal\n`;
    text += `💪 P: ${recipe.proteine} | C: ${recipe.carboidrati} | G: ${recipe.grassi}\n`;

    const ingredienti = Array.isArray(recipe.ingredienti) ? recipe.ingredienti : [];
    if (ingredienti.length) {
      text += `\n**Ingredienti:**\n`;
      ingredienti.forEach((ing) => {
        const s = typeof ing === 'string' ? ing : String(ing);
        if (s.trim()) text += `• ${s.trim()}\n`;
      });
    }

    // Accept several possible field names returned by the AI prompt
    // across languages (mirrors backend `/publish-recipe` behaviour).
    let procedimento =
      recipe.procedimento ||
      recipe.preparazione ||
      recipe.istruzioni ||
      recipe.instructions ||
      recipe.steps ||
      [];
    let steps = [];
    if (typeof procedimento === 'string') {
      steps = procedimento.split('\n').map((s) => s.trim()).filter(Boolean);
    } else if (Array.isArray(procedimento)) {
      steps = procedimento.map((s) => String(s).trim()).filter(Boolean);
    }
    if (steps.length) {
      text += `\n**Preparazione:**\n`;
      steps.forEach((step, idx) => {
        // Strip leading "1. " / "1) " / "1 - " already present.
        const stripped = step.replace(/^\s*\d+[.)\-]\s*/, '');
        text += `${idx + 1}. ${stripped}\n`;
      });
    }

    return text;
  }, [recipe]);

  useEffect(() => {
    // If no recipe data, go back
    if (!recipe) {
      navigate('/app/dashboard');
    } else {
      // Scroll to top when recipe is shown - with delay to ensure content is rendered
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'instant' });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        // Also try scrolling the app container
        const appContainer = document.querySelector('.h-screen.overflow-y-auto');
        if (appContainer) {
          appContainer.scrollTop = 0;
        }
      }, 50);
    }
  }, [recipe, navigate]);

  const handleSharePublished = () => {
    setPublished(true);
  };

  if (!recipe) return null;

  return (
    <div className={`min-h-screen pb-24 ${isDark ? 'bg-[#080D1A]' : 'bg-gray-50'}`} data-testid="ricetta-detail">
      {/* Sticky top navbar — always visible, includes back button and
          respects iOS safe-area-inset-top so the notch never overlaps
          the recipe content. */}
      <div
        className={`sticky top-0 z-30 backdrop-blur-md ${
          isDark ? 'bg-[#080D1A]/85 border-b border-white/10' : 'bg-white/90 border-b border-gray-200'
        }`}
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <div className="flex items-center gap-3 h-14 px-3">
          <button
            onClick={() => navigate(-1)}
            data-testid="ricetta-back-btn"
            className={`w-10 h-10 rounded-full flex items-center justify-center active:scale-95 transition-transform ${
              isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-900'
            }`}
            aria-label="Indietro"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h2
              className={`font-display text-base truncate ${isDark ? 'text-white' : 'text-gray-900'}`}
              title={recipe.nome}
            >
              {recipe.nome}
            </h2>
          </div>
        </div>
      </div>

      {/* Hero Image */}
      {imageUrl && (
        <div className="relative h-64">
          <img 
            src={imageUrl} 
            alt={recipe.nome}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

          {/* Title on image */}
          <div className="absolute bottom-0 left-0 right-0 p-6">
            <div className="flex items-center gap-2 mb-2">
              <ChefHat className="w-5 h-5 text-[#EC4899]" />
              <span className="text-sm text-white/80">{t('recipe.aiRecipe')}</span>
            </div>
            <h1 className="font-display text-2xl text-white">{recipe.nome}</h1>
          </div>
        </div>
      )}

      {/* No image header - with gradient based on meal type */}
      {!imageUrl && (
        <div className="relative overflow-hidden">
          {/* Gradient background based on meal type */}
          <div 
            className={`px-4 pt-4 pb-8 ${
              recipe.tipo_pasto === 'colazione' ? 'bg-gradient-to-br from-amber-400 to-orange-500' :
              recipe.tipo_pasto === 'pranzo' ? 'bg-gradient-to-br from-emerald-400 to-teal-500' :
              recipe.tipo_pasto === 'cena' ? 'bg-gradient-to-br from-indigo-400 to-purple-500' :
              recipe.tipo_pasto === 'spuntino' ? 'bg-gradient-to-br from-pink-400 to-rose-500' :
              'bg-gradient-to-br from-[#EC4899] to-[#F59E0B]'
            }`}
          >
            {/* Large icon */}
            <div className="flex justify-center mb-4">
              <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <ChefHat className="w-10 h-10 text-white" />
              </div>
            </div>
            
            {/* Title */}
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <span className="text-sm text-white/80 bg-white/20 px-3 py-1 rounded-full">
                  {t('recipe.aiRecipe')}
                </span>
              </div>
              <h1 className="font-display text-2xl text-white px-4">{recipe.nome}</h1>
            </div>
          </div>
          
          {/* Curved bottom */}
          <div className={`h-6 -mt-6 ${isDark ? 'bg-[#080D1A]' : 'bg-white'}`} style={{ borderRadius: '24px 24px 0 0' }}></div>
        </div>
      )}

      {/* Content */}
      <div className="px-4 py-6 space-y-6">
        {/* Description */}
        <p className={`text-base leading-relaxed ${isDark ? 'text-white/80' : 'text-gray-600'}`}>
          {recipe.descrizione}
        </p>

        {/* Nutritional Values - Row 1: Tempo + Calorie */}
        <div className="grid grid-cols-2 gap-3">
          <div className={`p-4 rounded-2xl text-center ${isDark ? 'bg-white/5' : 'bg-white border border-gray-100 shadow-[0_2px_8px_rgba(0,0,0,0.08)]'}`}>
            <Clock className="w-6 h-6 mx-auto mb-2 text-[#3B82F6]" />
            <p className={`text-sm mb-1 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{t('chef.prepTime')}</p>
            <p className={`font-bold text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {recipe.tempo_preparazione}
            </p>
          </div>
          <div className={`p-4 rounded-2xl text-center ${isDark ? 'bg-white/5' : 'bg-white border border-gray-100 shadow-[0_2px_8px_rgba(0,0,0,0.08)]'}`}>
            <Flame className="w-6 h-6 mx-auto mb-2 text-[#E8334A]" />
            <p className={`text-sm mb-1 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{t('chef.calories')}</p>
            <p className={`font-bold text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {recipe.calorie}
            </p>
          </div>
        </div>

        {/* Nutritional Values - Row 2: Proteine + Carboidrati + Grassi */}
        <div className="grid grid-cols-3 gap-3">
          <div className={`p-4 rounded-2xl text-center ${isDark ? 'bg-white/5' : 'bg-white border border-gray-100 shadow-[0_2px_8px_rgba(0,0,0,0.08)]'}`}>
            <Dumbbell className="w-6 h-6 mx-auto mb-2 text-[#16A34A]" />
            <p className={`text-sm mb-1 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{t('chef.protein')}</p>
            <p className={`font-bold text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {recipe.proteine}
            </p>
          </div>
          <div className={`p-4 rounded-2xl text-center ${isDark ? 'bg-white/5' : 'bg-white border border-gray-100 shadow-[0_2px_8px_rgba(0,0,0,0.08)]'}`}>
            <span className="text-xl mb-1 block">🍞</span>
            <p className={`text-sm mb-1 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{t('chef.carbs')}</p>
            <p className={`font-bold text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {recipe.carboidrati}
            </p>
          </div>
          <div className={`p-4 rounded-2xl text-center ${isDark ? 'bg-white/5' : 'bg-white border border-gray-100 shadow-[0_2px_8px_rgba(0,0,0,0.08)]'}`}>
            <Droplet className="w-6 h-6 mx-auto mb-2 text-[#F59E0B]" />
            <p className={`text-sm mb-1 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{t('chef.fat')}</p>
            <p className={`font-bold text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {recipe.grassi}
            </p>
          </div>
        </div>

        {/* Ingredients */}
        <div className={`p-5 rounded-2xl ${isDark ? 'bg-white/5' : 'bg-white shadow-sm'}`}>
          <h2 className={`font-display text-lg mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t('chef.ingredients')}
          </h2>
          <ul className="space-y-3">
            {recipe.ingredienti?.map((ing, i) => (
              <li key={i} className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-[#EC4899] mt-2 flex-shrink-0" />
                <span className={`${isDark ? 'text-white/80' : 'text-gray-700'}`}>{ing}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Preparation */}
        <div className={`p-5 rounded-2xl ${isDark ? 'bg-white/5' : 'bg-white shadow-sm'}`}>
          <h2 className={`font-display text-lg mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t('chef.instructions')}
          </h2>
          <ol className="space-y-4">
            {recipe.procedimento?.map((step, i) => (
              <li key={i} className="flex items-start gap-4">
                <div className="w-7 h-7 rounded-full bg-[#EC4899]/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-semibold text-[#EC4899]">{i + 1}</span>
                </div>
                <p className={`flex-1 pt-1 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>{step}</p>
              </li>
            ))}
          </ol>
        </div>
      </div>

      {/* Action button - centered share only */}
      <div className="px-4 pt-6 pb-8" data-testid="ricetta-action-buttons">
        <button
          onClick={published ? () => navigate('/app/feed') : () => setShowShareModal(true)}
          data-testid="btn-condividi-ricetta"
          className="w-full flex items-center justify-center gap-2 py-4 rounded-full font-semibold bg-[#E8334A] text-white hover:bg-[#d12a3f] transition-all"
        >
          {published ? (
            <>
              <Check className="w-5 h-5" />
              {t('recipe.goToFeed')}
            </>
          ) : (
            <>
              <Share2 className="w-5 h-5" />
              {t('common.share')}
            </>
          )}
        </button>
      </div>

      {/* Share modal — public feed or one/multiple challenges */}
      <ShareRecipeModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        isDark={isDark}
        recipe={recipe}
        imageUrl={imageUrl}
        text={postText}
        onPublished={handleSharePublished}
      />
    </div>
  );
}
